/**
 * The seven-beat onboarding (Phase 3), DERIVED from durable state so it
 * resumes correctly after any reload/import and never blocks veterans:
 *
 *   1. summons       — Herman's coach card on the Skills page
 *   2. train         — start Codex Whispering
 *   3. first-tick    — one real training tick (banks the starter ladle once)
 *   4. move-ladle    — move the ladle bank → inventory
 *   5. equip-ladle   — equip it
 *   6. accept-quest  — accept Roaches in /tmp
 *   7. clear-roaches — kill three Free-Disk Roaches (quest completes)
 *
 * A step also counts as done when ANY later step is done (backwards
 * cascade), so transient states (ladle re-banked after equipping, gear
 * swapped) can never walk a player backwards. The only one-time flag is the
 * starter grant — everything else is inferable from state.
 */
import type { PlayerState } from '../state';
import { isQuestComplete, questEntry } from './engine';
import { TUTORIAL_QUEST_ID } from './eligibility';

export const STARTER_LADLE_FLAG = 'starter_ladle';

export type TutorialStepId =
  | 'summons'
  | 'train'
  | 'first-tick'
  | 'move-ladle'
  | 'equip-ladle'
  | 'accept-quest'
  | 'clear-roaches';

export type TutorialStep = {
  id: TutorialStepId;
  /** Player-facing instruction (Herman's voice). */
  label: string;
  /** The page whose CTA advances this step. */
  page: 'skills' | 'bank' | 'inventory' | 'equipment' | 'quests' | 'combat';
  done: boolean;
};

export type TutorialView = {
  steps: ReadonlyArray<TutorialStep>;
  /** First incomplete step, or null when the tutorial is finished. */
  current: TutorialStepId | null;
  complete: boolean;
};

function holdsItem(slots: PlayerState['bank'] | PlayerState['inventory'], itemId: string): boolean {
  return slots.some((slot) => slot?.itemId === itemId);
}

export function deriveTutorial(state: PlayerState): TutorialView {
  const training =
    state.active.kind === 'train' && state.active.skillId === 'codex_whispering';
  const ticked = state.skills.codex_whispering.xp > 0;
  const ladleInInventory = holdsItem(state.inventory, 'polished_ladle');
  const anyEquipped = Object.values(state.equipment).some((slot) => slot !== null);
  const ladleEquipped = state.equipment.weapon === 'polished_ladle';
  const questHeld = questEntry(state, TUTORIAL_QUEST_ID) !== undefined;
  const questDone = isQuestComplete(state, TUTORIAL_QUEST_ID);

  const raw: Array<Omit<TutorialStep, 'done'> & { done: boolean }> = [
    {
      id: 'summons',
      label: 'Mr. Sparks, your summons. Pick a skill — Codex Whispering is polite.',
      page: 'skills',
      done: training || ticked,
    },
    {
      id: 'train',
      label: 'Start training Codex Whispering. I will continue through the night.',
      page: 'skills',
      done: training || ticked,
    },
    {
      id: 'first-tick',
      label: 'Complete one whisper. XP is not a feeling; it is a row.',
      page: 'skills',
      done: ticked,
    },
    {
      id: 'move-ladle',
      label: 'A Polished Ladle waits in the Bank. Move it to the inventory.',
      page: 'bank',
      done: ladleInInventory || ladleEquipped,
    },
    {
      id: 'equip-ladle',
      label: 'Equip the ladle. Equipment is manners with numbers.',
      page: 'equipment',
      done: ladleEquipped || anyEquipped,
    },
    {
      id: 'accept-quest',
      label: 'Accept "Roaches in /tmp" from the Quests page. The pantry is infested.',
      page: 'quests',
      done: questHeld,
    },
    {
      id: 'clear-roaches',
      label: 'Kill three Free-Disk Roaches. I will count; you will click Fight.',
      page: 'combat',
      done: questDone,
    },
  ];

  // Backwards cascade: any later done step marks earlier steps done.
  const steps: TutorialStep[] = [...raw];
  for (let i = steps.length - 2; i >= 0; i -= 1) {
    if (steps[i + 1]!.done) steps[i] = { ...steps[i]!, done: true };
  }

  const firstOpen = steps.find((s) => !s.done);
  return {
    steps,
    current: firstOpen?.id ?? null,
    complete: steps[steps.length - 1]!.done,
  };
}
